import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
import  useTheme  from '../context/ThemeContext';

export const PoliticaPrivacidade = () => {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'introducao',
      title: '1. Introdução',
      content: `A Atende24h ("nós", "nosso" ou "empresa") está comprometida em proteger a privacidade e os dados pessoais de seus usuários ("você" ou "usuário"). Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações quando você utiliza nossos serviços de automação de atendimento e softwares SaaS.

Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018) e outras legislações aplicáveis de proteção de dados.`
    },
    {
      id: 'dados-coletados',
      title: '2. Dados que Coletamos',
      content: `Coletamos os seguintes tipos de dados:`,
      items: [
        {
          subtitle: '2.1. Dados de Cadastro',
          text: 'Nome completo, e-mail, telefone/ WhatsApp, nome da empresa, CNPJ e cargo.'
        },
        {
          subtitle: '2.2. Dados de Uso',
          text: 'Informações sobre como você interage com nossos serviços, logs de acesso, dados de atendimento processados através de nossas plataformas.'
        },
        {
          subtitle: '2.3. Dados Técnicos',
          text: 'Endereço IP, tipo de navegador, provedor de internet, páginas de referência/saída, sistema operacional, data/hora de acesso.'
        },
        {
          subtitle: '2.4. Dados de Clientes Finais',
          text: 'Quando você utiliza nossos serviços de automação, processamos dados de seus próprios clientes (mensagens, contatos) em seu nome e por sua conta.'
        }
      ]
    },
    {
      id: 'uso-dados',
      title: '3. Como Usamos seus Dados',
      content: `Utilizamos seus dados pessoais para:`,
      items: [
        { text: 'Prestar, operar e manter nossos serviços' },
        { text: 'Processar transações e enviar notificações relacionadas' },
        { text: 'Fornecer suporte técnico e atendimento ao cliente' },
        { text: 'Enviar atualizações, newsletters e comunicações de marketing (com seu consentimento)' },
        { text: 'Cumprir obrigações legais e regulatórias' },
        { text: 'Melhorar e personalizar a experiência do usuário' },
        { text: 'Detectar, prevenir e resolver problemas técnicos ou de segurança' },
        { text: 'Gerar dados estatísticos e anonimizados para análise de mercado' }
      ]
    },
    {
      id: 'base-legal',
      title: '4. Base Legal para Tratamento',
      content: `Processamos seus dados pessoais com base nas seguintes fundamentações legais da LGPD:`,
      items: [
        { text: 'Execução de contrato ou de procedimentos preliminares (Art. 7º, V)' },
        { text: 'Cumprimento de obrigação legal ou regulatória (Art. 7º, II)' },
        { text: 'Exercício regular de direitos em processo judicial, administrativo ou arbitral (Art. 7º, VI)' },
        { text: 'Legítimo interesse do controlador ou de terceiro (Art. 7º, IX)' },
        { text: 'Consentimento do titular (Art. 7º, I) - quando aplicável' }
      ]
    },
    {
      id: 'compartilhamento',
      title: '5. Compartilhamento de Dados',
      content: `Seus dados pessoais podem ser compartilhados com:`,
      items: [
        {
          subtitle: '5.1. Prestadores de Serviço',
          text: 'Empresas que nos auxiliam na operação de nossos serviços (hospedagem cloud, processamento de pagamentos, ferramentas de comunicação).'
        },
        {
          subtitle: '5.2. Parceiros de Integração',
          text: 'Meta (WhatsApp Business API), provedores de serviços de mensagens e plataformas de automação quando necessário para a prestação do serviço.'
        },
        {
          subtitle: '5.3. Obrigação Legal',
          text: 'Autoridades públicas, quando requerido por lei, ordem judicial ou processo legal.'
        }
      ],
      note: 'Todos os terceiros com quem compartilhamos dados possuem contratos de confidencialidade e processamento de dados (DPA) em conformidade com a LGPD.'
    },
    {
      id: 'armazenamento',
      title: '6. Armazenamento e Segurança',
      content: `Adotamos medidas técnicas e organizacionais apropriadas para proteger seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição:`,
      items: [
        { text: 'Criptografia SSL/TLS para transmissão de dados' },
        { text: 'Criptografia AES-256 para dados em repouso' },
        { text: 'Firewalls e sistemas de detecção de intrusão' },
        { text: 'Controles de acesso baseados em função (RBAC)' },
        { text: 'Logs de auditoria e monitoramento contínuo' },
        { text: 'Backups criptografados e redundância geográfica' }
      ],
      extra: 'Seus dados são armazenados em servidores localizados no Brasil (AWS São Paulo), garantindo conformidade com a LGPD e menor latência.'
    },
    {
      id: 'retencao',
      title: '7. Retenção de Dados',
      content: `Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades descritas nesta política, incluindo:

- Dados de cadastro: enquanto sua conta estiver ativa e por até 5 anos após o encerramento (cumprimento de obrigações legais)
- Dados de atendimento: conforme configurado por você (padrão: 12 meses)
- Logs de acesso: 12 meses
- Backups: 30 dias

Após o período de retenção, seus dados são anonimizados ou excluídos de forma segura.`
    },
    {
      id: 'direitos',
      title: '8. Seus Direitos (LGPD)',
      content: `Como titular de dados pessoais, você possui os seguintes direitos:`,
      items: [
        { text: 'Confirmação da existência de tratamento' },
        { text: 'Acesso aos dados pessoais' },
        { text: 'Correção de dados incompletos, inexatos ou desatualizados' },
        { text: 'Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos' },
        { text: 'Portabilidade dos dados a outro fornecedor de serviço ou produto' },
        { text: 'Eliminação dos dados pessoais tratados com consentimento' },
        { text: 'Informação das entidades públicas e privadas com as quais compartilhamos dados' },
        { text: 'Informação sobre a possibilidade de não fornecer consentimento e suas consequências' },
        { text: 'Revogação do consentimento' }
      ],
      extra: 'Para exercer seus direitos, entre em contato através do e-mail: privacidade@atende24h.com.br. Responderemos em até 15 dias úteis.'
    },
    {
      id: 'cookies',
      title: '9. Cookies e Tecnologias Similares',
      content: `Utilizamos cookies e tecnologias similares para:`,
      items: [
        { text: 'Manter você autenticado em nossos sistemas' },
        { text: 'Lembrar suas preferências e configurações' },
        { text: 'Analisar o tráfego e comportamento de uso' },
        { text: 'Personalizar conteúdo e anúncios (com seu consentimento)' }
      ],
      extra: 'Você pode gerenciar suas preferências de cookies através das configurações do seu navegador. Note que a desativação de cookies essenciais pode afetar a funcionalidade dos serviços.'
    },
    {
      id: 'alteracoes',
      title: '10. Alterações nesta Política',
      content: `Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou por razões legais, operacionais ou regulatórias. 

Notificaremos você sobre alterações materiais através de:
- E-mail para o endereço cadastrado
- Aviso em destaque em nossos serviços
- Notificação no painel de controle

A data da última atualização será sempre indicada no início do documento.`
    },
    {
      id: 'contato',
      title: '11. Contato',
      content: `Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao tratamento de seus dados pessoais, entre em contato:

**Encarregado de Dados (DPO):**
- E-mail: privacidade@atende24h.com.br
- Telefone: (62) 99470-7272
- Endereço: [Endereço físico da empresa]

Responderemos em até 15 dias úteis. Se não estiver satisfeito com nossa resposta, você tem o direito de reclamar à Autoridade Nacional de Proteção de Dados (ANPD).`
    }
  ];

  const lastUpdate = '04 de Fevereiro de 2026';

  return (
    <div style={{ 
      marginTop: '80px',
      background: 'var(--bg-primary)',
      minHeight: '100vh',
    }}>
      {/* Header */}
      <section style={{
        padding: '4rem 0',
        background: isDark 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
        position: 'relative',
      }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '30px',
                  color: '#94a3b8',
                  fontSize: '0.85rem',
                  marginBottom: '1.5rem',
                }}
              >
                <i className="fas fa-shield-alt"></i>
                LGPD | Lei nº 13.709/2018
              </motion.div>

              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: 800,
                color: 'white',
                marginBottom: '1rem',
              }}>
                Política de Privacidade
              </h1>

              <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '1rem' }}>
                Sua privacidade é prioridade. Entenda como protegemos seus dados.
              </p>

              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '0.5rem' }}></i>
                Última atualização: {lastUpdate}
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Navegação Rápida */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        position: 'sticky',
        top: '80px',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
      }}>
        <div className="container">
          <div style={{
            display: 'flex',
            gap: '1rem',
            padding: '1rem 0',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s',
                  border: '1px solid var(--border-color)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--primary)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--bg-tertiary)';
                  e.target.style.color = 'var(--text-secondary)';
                }}
              >
                {section.title.split('. ')[1]}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <section style={{ padding: '4rem 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {sections.map((section, idx) => (
              <FadeIn key={section.id} delay={idx * 0.05}>
                <motion.div
                  id={section.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  style={{
                    marginBottom: '3rem',
                    padding: '2rem',
                    background: 'var(--card-bg)',
                    borderRadius: '16px',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <h2 style={{
                    fontSize: '1.5rem',
                    color: 'var(--text-primary)',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}>
                    <span style={{
                      width: '32px',
                      height: '32px',
                      background: 'var(--primary)',
                      color: 'white',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                    }}>
                      {section.title.split('.')[0]}
                    </span>
                    {section.title.split('. ')[1]}
                  </h2>

                  <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                    <p style={{ marginBottom: '1rem' }}>{section.content}</p>

                    {section.items && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0' }}>
                        {section.items.map((item, iidx) => (
                          <motion.li
                            key={iidx}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: iidx * 0.1 }}
                            style={{
                              padding: '1rem',
                              marginBottom: '0.75rem',
                              background: 'var(--bg-tertiary)',
                              borderRadius: '8px',
                              borderLeft: '3px solid var(--primary)',
                            }}
                          >
                            {item.subtitle && (
                              <h4 style={{ 
                                color: 'var(--text-primary)', 
                                marginBottom: '0.5rem',
                                fontSize: '1rem',
                              }}>
                                {item.subtitle}
                              </h4>
                            )}
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>{item.text}</p>
                          </motion.li>
                        ))}
                      </ul>
                    )}

                    {section.note && (
                      <div style={{
                        padding: '1rem',
                        background: 'rgba(37, 99, 235, 0.1)',
                        border: '1px solid rgba(37, 99, 235, 0.2)',
                        borderRadius: '8px',
                        marginTop: '1rem',
                        fontSize: '0.9rem',
                      }}>
                        <i className="fas fa-info-circle" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                        {section.note}
                      </div>
                    )}

                    {section.extra && (
                      <p style={{ 
                        marginTop: '1rem', 
                        padding: '1rem',
                        background: 'var(--bg-tertiary)',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                      }}>
                        {section.extra}
                      </p>
                    )}
                  </div>
                </motion.div>
              </FadeIn>
            ))}

            {/* Footer da Política */}
            <FadeIn>
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                background: 'var(--bg-secondary)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
              }}>
                <i className="fas fa-lock" style={{ 
                  fontSize: '3rem', 
                  color: 'var(--primary)',
                  marginBottom: '1rem',
                }}></i>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                  Sua Privacidade é Nossa Prioridade
                </h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Estamos comprometidos em proteger seus dados pessoais.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/contato" style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--primary)',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}>
                    Falar com DPO
                  </Link>
                  <Link to="/termos" style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '1px solid var(--border-color)',
                  }}>
                    Ver Termos de Uso
                  </Link>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};